import {CustomError} from "../../shared/errors/CustomErrors";
import {Option} from "../../shared/utilities/OptionT";
import {APIDao, Requester} from "../APIRequester";
import {INACTIVE_ORDER_REMINDER_API_PREFIX} from "../../common/constants";
import {
    DealershipExcludedRequest,
    DealershipRecipients,
    InactiveOrderReminderDealershipSettings,
    InactiveOrderReminderList,
    InactiveOrderReminderPendingCount,
    ProductExcludedRequest,
    SaveInactiveOrderReminderSettings,
    SendInactiveOrderReminderBatch,
} from "../../shared/entities/Order/InactiveOrderReminder";

export class InactiveOrderReminderRepository {
    private dao: Requester;

    constructor(dao: Requester) {
        this.dao = dao;
    }

    public async pendingCount(params: Record<string, unknown>): Promise<Option<InactiveOrderReminderPendingCount>> {
        return await this.dao
            .getRequest<InactiveOrderReminderPendingCount>(`${INACTIVE_ORDER_REMINDER_API_PREFIX}/pending-count`, {params})
            .then((res) => ({Some: res.data}))
            .catch((err: CustomError) => ({
                Some: {
                    count: 0,
                    monthsThreshold: 6,
                    defaultMonthsThreshold: 6,
                    allowedMonthsThresholds: [1, 3, 6, 9, 12],
                },
                None: err,
            }));
    }

    public async list(params: Record<string, unknown>): Promise<Option<InactiveOrderReminderList>> {
        return await this.dao.getRequest<InactiveOrderReminderList>(`${INACTIVE_ORDER_REMINDER_API_PREFIX}/list`, {params})
            .then(res => ({Some: res.data}))
            .catch((err: CustomError) => ({
                Some: {items: [], count: 0},
                None: err,
            }));
    }

    public async recipients(dealershipId: number): Promise<Option<DealershipRecipients>> {
        return await this.dao.getRequest<DealershipRecipients>(`${INACTIVE_ORDER_REMINDER_API_PREFIX}/recipients`, {
            params: {dealershipId},
        })
            .then(res => ({Some: res.data}))
            .catch((err: CustomError) => ({
                Some: {} as DealershipRecipients,
                None: err,
            }));
    }

    public async send(data: SendInactiveOrderReminderBatch): Promise<Option<unknown>> {
        return await this.dao.postRequest<unknown>(`${INACTIVE_ORDER_REMINDER_API_PREFIX}/send`, {data})
            .then(res => ({Some: res.data}))
            .catch((err: CustomError) => ({
                Some: null,
                None: err,
            }));
    }

    public async settings(dealershipIds: number[]): Promise<Option<InactiveOrderReminderDealershipSettings[]>> {
        return await this.dao.getRequest<InactiveOrderReminderDealershipSettings[]>(
            `${INACTIVE_ORDER_REMINDER_API_PREFIX}/settings`,
            {params: {dealershipIds: dealershipIds.join(",")}},
        )
            .then(res => ({Some: res.data}))
            .catch((err: CustomError) => ({
                Some: [],
                None: err,
            }));
    }

    public async saveSettings(data: SaveInactiveOrderReminderSettings): Promise<Option<void>> {
        return await this.dao.putRequest<void>(`${INACTIVE_ORDER_REMINDER_API_PREFIX}/settings`, {data})
            .then(() => ({Some: undefined}))
            .catch((err: CustomError) => ({
                Some: undefined,
                None: err,
            }));
    }

    public async setDealershipExcluded(data: DealershipExcludedRequest): Promise<Option<void>> {
        return await this.dao.putRequest<void>(`${INACTIVE_ORDER_REMINDER_API_PREFIX}/dealership-excluded`, {data})
            .then(() => ({Some: undefined}))
            .catch((err: CustomError) => ({
                Some: undefined,
                None: err,
            }));
    }

    public async setProductExcluded(data: ProductExcludedRequest): Promise<Option<void>> {
        return await this.dao.putRequest<void>(`${INACTIVE_ORDER_REMINDER_API_PREFIX}/product-excluded`, {data})
            .then(() => ({Some: undefined}))
            .catch((err: CustomError) => ({
                Some: undefined,
                None: err,
            }));
    }
}

export const InactiveOrderReminderRepositoryInstance = new InactiveOrderReminderRepository(APIDao);
